import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Modal,
  Form,
  Button,
  Alert,
  ProgressBar,
  Tabs,
  Tab,
  Table,
  Badge,
  ListGroup
} from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { userService, emailTemplateService } from '../../services/api';
import { isMinor } from '../../utils/userHelpers';

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'];
const MAX_ATTACHMENTS_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (fileName) => {
  const ext = fileName ? fileName.split('.').pop().toLowerCase() : '';
  if (ext === 'pdf') return 'fas fa-file-pdf text-danger';
  if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word text-primary';
  if (['xls', 'xlsx'].includes(ext)) return 'fas fa-file-excel text-success';
  if (['jpg', 'jpeg', 'png'].includes(ext)) return 'fas fa-file-image text-warning';
  return 'fas fa-file text-secondary';
};

const SendBroadcastEmailModal = ({ 
  show, 
  onHide, 
  selectedUserIds = [],
  selectedUsers = [],
  emailAddresses = [],
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState({
    subject: '',
    body: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [sendingProgress, setSendingProgress] = useState(0);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('compose');
  const quillRef = useRef(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const isDuplicateEmailWarning = error.toLowerCase().includes('stessa email');

  // Load user objects if only IDs are provided
  useEffect(() => {
    if (show) {
      if (selectedUsers && selectedUsers.length > 0) {
        setFetchedUsers([]);
        setLoadingUsers(false);
      } else if (selectedUserIds && selectedUserIds.length > 0) {
        setLoadingUsers(true);
        Promise.all(selectedUserIds.map(id => userService.getUser(id)))
          .then(results => {
            const loaded = results
              .filter(r => r && r.success && r.data)
              .map(r => r.data);
            setFetchedUsers(loaded);
          })
          .catch(err => {
            console.error('Error fetching users for broadcast modal:', err);
          })
          .finally(() => {
            setLoadingUsers(false);
          });
      } else {
        setFetchedUsers([]);
        setLoadingUsers(false);
      }
    }
  }, [show, selectedUsers, selectedUserIds]);

  const currentUsers = useMemo(() => {
    if (selectedUsers && selectedUsers.length > 0) {
      return selectedUsers;
    }
    return fetchedUsers;
  }, [selectedUsers, fetchedUsers]);

  // Resolves email recipient for a user (same logic as SendBulkAthleteAccessModal)
  const resolveRecipient = useCallback((user) => {
    if (!user) return { email: null, type: 'none', label: 'Nessuna email' };

    const minor = isMinor(user.dateOfBirth, user.fiscalCode);

    if (minor) {
      if (user.parent?.email && user.parent.email.trim()) {
        const parentName = [user.parent.firstName, user.parent.lastName].filter(Boolean).join(' ');
        return {
          email: user.parent.email.trim(),
          type: 'parent',
          label: parentName ? `Genitore (${parentName})` : 'Genitore'
        };
      }
      const athleteEmail = (user.email && user.email.trim()) || (user.loginEmail && user.loginEmail.trim());
      if (athleteEmail) {
        return {
          email: athleteEmail,
          type: 'minor_athlete_fallback',
          label: 'Atleta (fallback: genitore senza email)'
        };
      }
      return {
        email: null,
        type: 'none',
        label: 'Genitore e atleta senza email'
      };
    }

    if (user.email && user.email.trim()) {
      return {
        email: user.email.trim(),
        type: 'user',
        label: 'Email atleta'
      };
    }

    if (user.loginEmail && user.loginEmail.trim()) {
      return {
        email: user.loginEmail.trim(),
        type: 'login',
        label: 'Email login'
      };
    }

    return { email: null, type: 'none', label: 'Nessuna email' };
  }, []);

  // Analyzed recipients list
  const analyzedRecipients = useMemo(() => {
    const list = currentUsers.map(user => {
      const recipient = resolveRecipient(user);
      return {
        id: user.id,
        user,
        name: [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Atleta',
        fiscalCode: user.fiscalCode,
        email: recipient.email,
        type: recipient.type,
        label: recipient.label,
        isValid: Boolean(recipient.email),
        isDirect: false
      };
    });

    // Include direct email addresses if provided
    if (emailAddresses && emailAddresses.length > 0) {
      emailAddresses.forEach((email, idx) => {
        const trimmed = email ? email.trim() : '';
        list.push({
          id: `direct-${idx}`,
          user: null,
          name: 'Destinatario Diretto',
          fiscalCode: null,
          email: trimmed || null,
          type: 'direct',
          label: 'Email diretta',
          isValid: Boolean(trimmed),
          isDirect: true
        });
      });
    }

    return list;
  }, [currentUsers, emailAddresses, resolveRecipient]);

  const validRecipients = useMemo(() => {
    return analyzedRecipients.filter(r => r.isValid);
  }, [analyzedRecipients]);

  const missingRecipients = useMemo(() => {
    return analyzedRecipients.filter(r => !r.isValid);
  }, [analyzedRecipients]);

  // Duplicate email detection
  const duplicateEmails = useMemo(() => {
    const counts = {};
    validRecipients.forEach(item => {
      if (item.email) {
        const lower = item.email.toLowerCase();
        counts[lower] = (counts[lower] || 0) + 1;
      }
    });
    return new Set(Object.keys(counts).filter(e => counts[e] > 1));
  }, [validRecipients]);

  // Available placeholders
  const placeholders = useMemo(() => ([
    { tag: '{nome}', label: 'Nome', preview: 'Mario' },
    { tag: '{cognome}', label: 'Cognome', preview: 'Rossi' }
  ]), []);

  // Insert placeholder at cursor position in Quill editor
  const insertPlaceholder = useCallback((tag) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, tag);
      editor.setSelection(range.index + tag.length);
    } else {
      // Fallback: append to body
      setFormData(prev => ({
        ...prev,
        body: prev.body.replace('</p>', `${tag}</p>`)
      }));
    }
  }, []);

  // Replace placeholders with preview values
  const replacePreviewPlaceholders = useCallback((text) => {
    if (!text) return text;
    let result = text;
    placeholders.forEach(p => {
      result = result.replaceAll(p.tag, p.preview);
    });
    return result;
  }, [placeholders]);

  // Total attachments size
  const totalAttachmentsSize = useMemo(() => {
    return attachments.reduce((sum, f) => sum + f.size, 0);
  }, [attachments]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setFormData({ subject: '', body: '' });
      setAttachments([]);
      setError('');
      setSendingProgress(0);
      setActiveTab('compose');
      // Load email templates
      emailTemplateService.getEmailTemplates().then(result => {
        if (result.success && result.data) {
          setEmailTemplates(result.data);
        }
      });
    }
  }, [show]);

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLoadTemplate = (templateId) => {
    if (!templateId) return;
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        subject: template.subject,
        body: template.body
      }));
    }
  };

  const handleFilesAdded = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const newFiles = Array.from(fileList);
    let currentTotal = attachments.reduce((sum, f) => sum + f.size, 0);
    const validFiles = [];
    let errorMsg = '';

    for (const file of newFiles) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        errorMsg = `Il file "${file.name}" non è in un formato consentito. Formati ammessi: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Immagini (.jpg, .jpeg, .png).`;
        break;
      }
      if (currentTotal + file.size > MAX_ATTACHMENTS_SIZE_BYTES) {
        errorMsg = `L'aggiunta del file "${file.name}" supera il limite massimo consentito di 10 MB complessivi per email.`;
        break;
      }
      // Evita duplicati identici
      if (attachments.some(a => a.name === file.name && a.size === file.size)) {
        continue;
      }
      currentTotal += file.size;
      validFiles.push(file);
    }

    if (errorMsg) {
      setError(errorMsg);
    } else {
      setError('');
      setAttachments(prev => [...prev, ...validFiles]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Compilare tutti i campi obbligatori');
      return;
    }

    if (validRecipients.length === 0) {
      setError('Nessun destinatario valido con indirizzo email selezionato');
      return;
    }

    if (duplicateEmails.size > 0) {
      setError('Sono presenti destinatari con la stessa email. Rimuovere i duplicati prima di inviare.');
      return;
    }

    if (totalAttachmentsSize > MAX_ATTACHMENTS_SIZE_BYTES) {
      setError('La dimensione complessiva degli allegati supera il limite massimo di 10 MB.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSendingProgress(0);

      // Simulate progress (il backend fa tutto in una chiamata)
      const progressInterval = setInterval(() => {
        setSendingProgress(prev => {
          if (prev >= 90) return prev;
          return prev + 10;
        });
      }, 200);

      const recipientUserIds = validRecipients.filter(r => !r.isDirect).map(r => r.id);
      const directEmails = validRecipients.filter(r => r.isDirect).map(r => r.email);

      const userIdsToSend = recipientUserIds.length > 0 
        ? recipientUserIds 
        : (currentUsers.length === 0 && selectedUserIds.length > 0 ? selectedUserIds : []);

      const response = await userService.sendBroadcastEmail(
        userIdsToSend,
        formData.subject,
        formData.body,
        directEmails.length > 0 ? directEmails : null,
        attachments
      );

      clearInterval(progressInterval);
      setSendingProgress(100);

      if (response.success) {
        const data = response.data;
        const message = data.totalFailed === 0 
          ? `✅ ${data.totalSent} email inviate con successo!`
          : `⚠️ Inviate ${data.totalSent} email, ${data.totalFailed} fallite.`;
        
        if (onSuccess) {
          onSuccess(message);
        }
        
        // Reset and close
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setError(response.data?.message || response.message || 'Errore nell\'invio delle email');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error sending broadcast email:', err);
      if (onError) {
        onError('Errore nell\'invio delle email');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Compilare oggetto e corpo prima di inviare un\'email di test');
      return;
    }

    if (totalAttachmentsSize > MAX_ATTACHMENTS_SIZE_BYTES) {
      setError('La dimensione complessiva degli allegati supera il limite massimo di 10 MB.');
      return;
    }

    try {
      setError('');
      // Get current user ID from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) {
        setError('Utente non trovato. Effettuare nuovamente il login.');
        return;
      }

      const response = await userService.sendBroadcastEmail(
        [user.id], // Send only to current user
        `[TEST] ${formData.subject}`,
        formData.body,
        null,
        attachments
      );

      if (response.success) {
        alert('✅ Email di test inviata con successo! Controlla la tua casella di posta.');
      } else {
        setError(response.data?.message || 'Errore nell\'invio dell\'email di test');
      }
    } catch (err) {
      setError('Errore nell\'invio dell\'email di test');
      console.error('Error sending test email:', err);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setFormData({ subject: '', body: '' });
      setAttachments([]);
      setSendingProgress(0);
      setActiveTab('compose');
      onHide();
    }
  };

  // Quill editor modules configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link'
  ];

  const totalCount = analyzedRecipients.length;

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          <i className="fas fa-envelope me-2"></i>
          Invia Email Massiva ({totalCount} {totalCount === 1 ? 'destinatario selezionato' : 'destinatari selezionati'})
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant={isDuplicateEmailWarning ? 'warning' : 'danger'} onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}

          {/* Missing email alert */}
          {missingRecipients.length > 0 && (
            <Alert variant="warning" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Attenzione:</strong> {missingRecipients.length} {missingRecipients.length === 1 ? 'destinatario selezionato non ha' : 'destinatari selezionati non hanno'} un indirizzo email configurato e {missingRecipients.length === 1 ? 'verrà escluso' : 'verranno esclusi'} dall'invio.
              <span className="ms-2">
                (Destinatari pronti per l'invio: <strong>{validRecipients.length}</strong> su {totalCount})
              </span>
            </Alert>
          )}

          {/* Duplicate email alert */}
          {duplicateEmails.size > 0 && (
            <Alert variant="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Attenzione:</strong> {duplicateEmails.size} {duplicateEmails.size === 1 ? 'indirizzo email è condiviso' : 'indirizzi email sono condivisi'} da più destinatari. Il server richiede email univoche per ciascun destinatario nell'invio massivo.
            </Alert>
          )}

          {missingRecipients.length === 0 && duplicateEmails.size === 0 && (
            <Alert variant="info" className="mb-3">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Destinatari selezionati:</strong> {totalCount}
            </Alert>
          )}

          {loading && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Invio in corso...</span>
                <span>{sendingProgress}%</span>
              </div>
              <ProgressBar 
                now={sendingProgress} 
                variant={sendingProgress === 100 ? 'success' : 'primary'}
                animated={sendingProgress < 100}
              />
            </div>
          )}

          <Tabs 
            activeKey={activeTab} 
            onSelect={(k) => setActiveTab(k)} 
            className="mb-3"
          >
            <Tab eventKey="compose" title="Componi Email">
              {emailTemplates.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>Carica da template pre-impostato</Form.Label>
                  <Form.Select
                    onChange={(e) => handleLoadTemplate(e.target.value)}
                    disabled={loading}
                    defaultValue=""
                  >
                    <option value="">— Seleziona un template —</option>
                    {emailTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Seleziona un template per caricare oggetto e corpo email
                  </Form.Text>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Oggetto *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Oggetto dell'email"
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  disabled={loading}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Corpo Email (HTML) *</Form.Label>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.body}
                  onChange={(value) => handleChange('body', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Scrivi il contenuto dell'email..."
                  readOnly={loading}
                  style={{ height: '300px', marginBottom: '50px' }}
                />
              </Form.Group>

              <Alert variant="info" className="mt-3">
                <i className="fas fa-tags me-2"></i>
                <strong>Variabili disponibili:</strong> clicca per inserire nel corpo dell'email. Verranno sostituite con i dati di ciascun destinatario.
                <div className="mt-2 d-flex gap-2 flex-wrap">
                  {placeholders.map(p => (
                    <Button
                      key={p.tag}
                      variant="outline-primary"
                      size="sm"
                      disabled={loading}
                      onClick={() => insertPlaceholder(p.tag)}
                    >
                      <i className="fas fa-plus-circle me-1"></i>
                      {p.tag} <span className="text-muted">({p.label})</span>
                    </Button>
                  ))}
                </div>
                <div className="mt-1">
                  <small className="text-muted">Puoi anche digitare manualmente {'{nome}'} e {'{cognome}'} nell'oggetto o nel corpo.</small>
                </div>
              </Alert>
            </Tab>

            {/* Tab Allegati */}
            <Tab 
              eventKey="attachments" 
              title={
                <span>
                  <i className="fas fa-paperclip me-1"></i>
                  Allegati {attachments.length > 0 && <Badge bg="primary" pill className="ms-1">{attachments.length}</Badge>}
                </span>
              }
            >
              <div className="mb-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  onChange={(e) => handleFilesAdded(e.target.files)}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  data-testid="broadcast-attachments-input"
                />

                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    handleFilesAdded(e.dataTransfer.files);
                  }}
                  className={`border border-2 rounded p-4 text-center ${
                    isDragging ? 'border-primary bg-light' : 'border-dashed'
                  }`}
                  style={{ borderStyle: 'dashed', cursor: 'pointer', backgroundColor: isDragging ? '#e9ecef' : '#f8f9fa' }}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="attachments-dropzone"
                >
                  <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-2"></i>
                  <h5 className="mb-1">Trascina qui i file oppure clicca per selezionarli</h5>
                  <p className="text-muted small mb-1">
                    Formati ammessi: <strong>PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Immagini (.jpg, .jpeg, .png)</strong>
                  </p>
                  <p className="text-muted small mb-0">
                    Limite complessivo: <strong>10 MB</strong>
                  </p>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <strong>Dimensione totale allegati:</strong>{' '}
                    <span className={totalAttachmentsSize > MAX_ATTACHMENTS_SIZE_BYTES ? 'text-danger fw-bold' : 'text-muted'}>
                      {formatFileSize(totalAttachmentsSize)} / 10 MB
                    </span>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={loading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className="fas fa-plus me-1"></i> Aggiungi altri file
                  </Button>
                </div>

                {attachments.length > 0 && (
                  <div className="mt-3">
                    <h6 className="mb-2">File selezionati ({attachments.length}):</h6>
                    <ListGroup>
                      {attachments.map((file, idx) => (
                        <ListGroup.Item
                          key={`${file.name}-${idx}`}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div className="d-flex align-items-center text-truncate me-3">
                            <i className={`${getFileIcon(file.name)} fa-lg me-3`}></i>
                            <div className="text-truncate">
                              <span className="fw-bold d-block text-truncate">{file.name}</span>
                              <small className="text-muted">{formatFileSize(file.size)}</small>
                            </div>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={loading}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveAttachment(idx);
                            }}
                            title="Rimuovi allegato"
                            aria-label={`Rimuovi ${file.name}`}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}
              </div>
            </Tab>

            <Tab eventKey="preview" title="Anteprima">
              <Alert variant="secondary" className="mb-3">
                <i className="fas fa-eye me-2"></i>
                I placeholder vengono sostituiti con dati di esempio (Mario Rossi).
              </Alert>
              <div className="border rounded p-3 bg-light">
                <div className="mb-3">
                  <strong>Oggetto:</strong>
                  <div className="mt-1">
                    {formData.subject 
                      ? replacePreviewPlaceholders(formData.subject) 
                      : <em className="text-muted">Nessun oggetto</em>}
                  </div>
                </div>
                <hr />
                <div>
                  <strong>Corpo:</strong>
                  <div 
                    className="mt-2 p-3 bg-white border rounded"
                    style={{ minHeight: '200px', maxHeight: '400px', overflow: 'auto' }}
                    dangerouslySetInnerHTML={{ 
                      __html: replacePreviewPlaceholders(formData.body) || '<em class="text-muted">Nessun contenuto</em>' 
                    }}
                  />
                </div>

                {attachments.length > 0 && (
                  <>
                    <hr />
                    <div>
                      <strong>Allegati ({attachments.length}):</strong>
                      <div className="mt-2 d-flex flex-wrap gap-2">
                        {attachments.map((file, idx) => (
                          <Badge key={idx} bg="secondary" className="p-2 d-inline-flex align-items-center">
                            <i className={`${getFileIcon(file.name)} me-2`}></i>
                            {file.name} ({formatFileSize(file.size)})
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Tab>

            {/* Tab Destinatari */}
            <Tab eventKey="recipients" title={`Destinatari (${totalCount})`}>
              {loadingUsers ? (
                <div className="text-center py-4">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Caricamento destinatari...
                </div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table responsive hover size="sm">
                    <thead>
                      <tr>
                        <th>Atleta</th>
                        <th>Email di Destinazione</th>
                        <th>Tipo</th>
                        <th>Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyzedRecipients.map(item => {
                        const isDup = item.email && duplicateEmails.has(item.email.toLowerCase());
                        return (
                          <tr key={item.id} className={!item.isValid ? 'table-warning' : isDup ? 'table-danger' : ''}>
                            <td>
                              <strong>{item.name}</strong>
                              {item.fiscalCode && (
                                <small className="text-muted d-block">{item.fiscalCode}</small>
                              )}
                            </td>
                            <td>
                              {item.email ? (
                                <span className="font-monospace">{item.email}</span>
                              ) : (
                                <span className="text-danger fst-italic">Mancante</span>
                              )}
                            </td>
                            <td>
                              {item.type === 'minor_athlete_fallback' ? (
                                <Badge bg="warning" text="dark" className="d-inline-flex align-items-center">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  {item.label}
                                </Badge>
                              ) : (
                                <small className="text-muted">{item.label}</small>
                              )}
                            </td>
                            <td>
                              {!item.isValid ? (
                                <Badge bg="warning" text="dark">
                                  <i className="fas fa-times me-1"></i>
                                  Escluso (Senza Email)
                                </Badge>
                              ) : isDup ? (
                                <Badge bg="danger">
                                  <i className="fas fa-exclamation-circle me-1"></i>
                                  Email Duplicata
                                </Badge>
                              ) : (
                                <Badge bg="success">
                                  <i className="fas fa-check me-1"></i>
                                  Pronto
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button 
              variant="outline-secondary" 
              onClick={handleSendTestEmail}
              disabled={loading || !formData.subject.trim() || !formData.body.trim()}
            >
              <i className="fas fa-paper-plane me-2"></i>
              Invia Test a Me Stesso
            </Button>
          </div>
          <div>
            <Button 
              variant="secondary" 
              onClick={handleClose}
              disabled={loading}
              className="me-2"
            >
              Annulla
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading || validRecipients.length === 0 || duplicateEmails.size > 0 || !formData.subject.trim() || !formData.body.trim()}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Invio...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-2"></i>
                  Invia a {validRecipients.length} {validRecipients.length === 1 ? 'Destinatario' : 'Destinatari'}
                  {attachments.length > 0 ? ` (${attachments.length} allegat${attachments.length === 1 ? 'o' : 'i'})` : ''}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SendBroadcastEmailModal;
